import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Evento } from '../../types/eventos';
import { useFestivosQuery } from '../../services/hooks/useFestivos';
import { useCreateMultipleEventosMutation } from '../../services/hooks/useEventos';
import { formatDateToLocal } from '../../utils/utils';
import { useBranding } from '../../contexts/BrandingContext';

interface ReplicarEventoProps {
    event: Evento;
    onClose: () => void;
    startDate: string;
    endDate: string;
}

const ReplicarEvento: React.FC<ReplicarEventoProps> = ({ 
    event, 
    onClose, 
    startDate, 
    endDate 
}) => {
    const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
    const [fechaFin, setFechaFin] = useState<Date | null>(null);
    const [alternarSalas, setAlternarSalas] = useState<boolean>(false);
    const [mostrarVistaPrevia, setMostrarVistaPrevia] = useState<boolean>(false);
    const [eventosParaCrear, setEventosParaCrear] = useState<Evento[]>([]);
    const [creandoEventos, setCreandoEventos] = useState<boolean>(false);
    const [progreso, setProgreso] = useState<number>(0);

    // Consultar festivos y mutación para crear eventos
    const { data: festivos } = useFestivosQuery();
    const createMultipleEventosMutation = useCreateMultipleEventosMutation(startDate, endDate);

    // Obtener salas disponibles desde branding
    const { branding } = useBranding();
    const salasDisponibles = branding?.rooms ?? [];

    // Función para obtener la sala alternativa (alterna por índice en la lista de salas)
    const getSalaAlternativa = (salaActual: string): string => {
        if (salasDisponibles.length < 2) return salaActual;
        const currentIndex = salasDisponibles.findIndex((r) => r.name === salaActual);
        if (currentIndex === -1) return salaActual;
        const nextIndex = (currentIndex + 1) % salasDisponibles.length;
        return salasDisponibles[nextIndex].name;
    };

    // Función para calcular la sala según la semana (para usar en la vista previa)
    const calcularSala = (semanaIndex: number): string => {
        if (!alternarSalas) {
            return event.sala;
        }
        
        // Semanas pares = sala original, semanas impares = sala alternativa
        if (semanaIndex % 2 === 0) {
            return event.sala;
        } else {
            return getSalaAlternativa(event.sala);
        }
    };

    // Función para calcular la fecha específica según el día de la semana del evento original
    const calcularFechaEvento = (fechaSemana: Date): Date => {
        const fechaOriginalEvento = new Date(event.fecha);
        const diaOriginalSemana = fechaOriginalEvento.getDay(); // 0=domingo, 1=lunes, etc.
        
        const fecha = new Date(fechaSemana);
        const diaActual = fecha.getDay();
        const diferenciaDias = diaOriginalSemana - diaActual;
        
        fecha.setDate(fecha.getDate() + diferenciaDias);
        return fecha;
    };

    // Función para verificar si una fecha es festivo
    const esFestivo = (fecha: Date): boolean => {
        if (!festivos) return false;
        
        const fechaStr = fecha.toISOString().split('T')[0];
        return festivos.some(festivo => {
            const festivoDate = new Date(festivo.fecha);
            const festivoLocal = new Date(festivoDate.getTime() - (festivoDate.getTimezoneOffset() * 60000));
            return festivoLocal.toISOString().split('T')[0] === fechaStr;
        });
    };

    // Función principal para calcular todos los eventos a crear
    const calcularEventosACrear = (): Evento[] => {
        if (!fechaInicio || !fechaFin) return [];

        const eventos: Evento[] = [];
        const fechaIteracion = new Date(fechaInicio);
        const fechaLimite = new Date(fechaFin);
        let semanaIndex = 0;

        // Usar formatDateToLocal para evitar problemas de timezone
        const fechaEventoOriginalDate = new Date(event.fecha);
        const fechaOriginalStr = formatDateToLocal(fechaEventoOriginalDate);

        while (fechaIteracion <= fechaLimite) {
            // Calcular la fecha específica del evento para esta semana
            const fechaEvento = calcularFechaEvento(fechaIteracion);
            
            // Verificar que la fecha esté dentro del rango
            if (fechaEvento >= fechaInicio && fechaEvento <= fechaLimite) {
                // Usar formatDateToLocal para comparación consistente
                const fechaEventoStr = formatDateToLocal(fechaEvento);
                
                // VALIDACIÓN CRÍTICA: Excluir la fecha del evento original
                const esEventoOriginal = fechaEventoStr === fechaOriginalStr;
                
                if (!esEventoOriginal) {
                    // Verificar que no sea festivo
                    if (!esFestivo(fechaEvento)) {
                        // Verificar que tenemos instructor y nivel válidos
                        if (!event.instructor?._id || !event.nivel?._id) {
                            console.error('❌ Evento sin instructor o nivel válido:', event);
                            return eventos; // Retornar lo que tenemos hasta ahora
                        }

                        // Normalizar fecha para MongoDB
                        const fechaLocal = new Date(fechaEvento.getTime() - (fechaEvento.getTimezoneOffset() * 60000));
                        
                        eventos.push({
                            nivel_id: event.nivel._id,
                            instructor_id: event.instructor._id,
                            sala: calcularSala(semanaIndex),
                            fecha: fechaLocal.toISOString(),
                            horaInicio: event.horaInicio,
                            horaFin: event.horaFin,
                            maxClientes: event.maxClientes || 10,
                            cancelado: false,
                            clientesInscritos: []
                        });
                    }
                }
            }

            // Avanzar a la siguiente semana
            fechaIteracion.setDate(fechaIteracion.getDate() + 7);
            semanaIndex++;
        }

        return eventos;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!fechaInicio || !fechaFin) {
            return;
        }

        if (fechaInicio >= fechaFin) {
            return;
        }

        // Sin nivel o instructor (borrados) no se pueden crear las copias.
        if (!event.nivel || !event.instructor) {
            toast.error('Esta clase tiene un nivel o instructor eliminado. Edítala y asígnale uno antes de replicarla.');
            return;
        }

        // Calcular eventos a crear y mostrar vista previa
        const eventos = calcularEventosACrear();
        setEventosParaCrear(eventos);
        setMostrarVistaPrevia(true);
        
        console.log(`Se crearán ${eventos.length} eventos`);
    };

    // Función para volver al formulario desde la vista previa
    const handleVolverFormulario = () => {
        setMostrarVistaPrevia(false);
        setEventosParaCrear([]);
    };

    // Función para crear los eventos masivamente
    const handleCrearEventos = async () => {
        if (eventosParaCrear.length === 0) {
            return;
        }

        setCreandoEventos(true);
        setProgreso(0);

        try {
            console.log('🚀 Iniciando creación de eventos:', eventosParaCrear);

            // Simular progreso antes de la llamada
            setProgreso(10);

            await createMultipleEventosMutation.mutateAsync(eventosParaCrear);

            // Progreso completo
            setProgreso(100);

            console.log('✅ Eventos creados exitosamente');

            // Cerrar modal después de un pequeño delay para mostrar el 100%
            setTimeout(() => {
                onClose();
            }, 1000);

        } catch (error) {
            console.error('❌ Error al crear eventos:', error);
            setProgreso(0);
        } finally {
            setCreandoEventos(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-full overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                
                <div className="p-6">
                    {!mostrarVistaPrevia ? (
                        <>
                            <h2 className="text-2xl font-bold mb-4 text-center">Replicar Evento</h2>
                            
                            {/* Información del evento base */}
                            <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                                <h3 className="font-bold text-lg mb-2">Evento a replicar:</h3>
                                <p className="text-sm"><strong>Nivel:</strong> {event.nivel?.nombre}</p>
                                <p className="text-sm"><strong>Instructor:</strong> {event.instructor?.nombre}</p>
                                <p className="text-sm"><strong>Sala:</strong> {event.sala}</p>
                                <p className="text-sm"><strong>Horario:</strong> {event.horaInicio} - {event.horaFin}</p>
                                <p className="text-sm"><strong>Día de la semana:</strong> {new Date(event.fecha).toLocaleDateString('es-ES', { weekday: 'long' })}</p>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2">
                                        Fecha de inicio:
                                    </label>
                                    <DatePicker
                                        selected={fechaInicio}
                                        onChange={(date) => setFechaInicio(date)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                        placeholderText="Seleccione fecha de inicio"
                                        minDate={new Date()} // No permitir fechas pasadas
                                    />
                                </div>

                                <div className="mb-6">
                                    <label className="block text-gray-700 text-sm font-bold mb-2">
                                        Fecha de fin:
                                    </label>
                                    <DatePicker
                                        selected={fechaFin}
                                        onChange={(date) => setFechaFin(date)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                        placeholderText="Seleccione fecha de fin"
                                        minDate={fechaInicio || new Date()} // Fecha fin no puede ser menor que fecha inicio
                                    />
                                </div>

                                {/* Checkbox para alternar salas */}
                                <div className="mb-6">
                                    <div className="flex items-center">
                                        <input
                                            id="alternar-salas"
                                            type="checkbox"
                                            checked={alternarSalas}
                                            onChange={(e) => setAlternarSalas(e.target.checked)}
                                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="alternar-salas" className="ml-2 block text-sm text-gray-900">
                                            Alternar salas cada semana
                                        </label>
                                    </div>
                                    {alternarSalas && (
                                        <div className="mt-2 text-xs text-gray-600">
                                            <p>ℹ️ Alternancia:</p>
                                            <p>• Semana 1, 3, 5... → <strong>{event.sala}</strong></p>
                                            <p>• Semana 2, 4, 6... → <strong>{getSalaAlternativa(event.sala)}</strong></p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    >
                                        Vista Previa
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-bold mb-4 text-center">Vista Previa de Eventos</h2>
                            
                            {eventosParaCrear.length > 0 ? (
                                <>
                                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            <strong>Se crearán {eventosParaCrear.length} eventos nuevos</strong> basados en el evento original.
                                        </p>
                                    </div>

                                    {/* Tabla de eventos */}
                                    <div className="mb-6 max-h-96 overflow-y-auto">
                                        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                        #
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                        Fecha
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                        Día
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                        Sala
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                        Horario
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {eventosParaCrear.map((evento, index) => {
                                                    const fechaEvento = new Date(evento.fecha);
                                                    return (
                                                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                {fechaEvento.toLocaleDateString('es-ES')}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                {fechaEvento.toLocaleDateString('es-ES', { weekday: 'long' })}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-purple-600">
                                                                {evento.sala}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                {evento.horaInicio} - {evento.horaFin}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="flex justify-between gap-2">
                                        <button
                                            type="button"
                                            onClick={handleVolverFormulario}
                                            disabled={creandoEventos}
                                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            ← Volver
                                        </button>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                disabled={creandoEventos}
                                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCrearEventos}
                                                disabled={creandoEventos}
                                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {creandoEventos ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Creando...
                                                    </>
                                                ) : (
                                                    '✓ Crear Eventos'
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Barra de progreso */}
                                    {creandoEventos && (
                                        <div className="mt-4">
                                            <div className="bg-gray-200 rounded-full h-2">
                                                <div 
                                                    className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                                    style={{ width: `${progreso}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-center text-sm text-gray-600 mt-2">
                                                Creando eventos... {progreso}%
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 mb-4">No se encontraron eventos para crear en el rango seleccionado.</p>
                                    <button
                                        type="button"
                                        onClick={handleVolverFormulario}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                        ← Volver
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReplicarEvento;